import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  INITIAL_REGISTRATION,
  RENEW_REGISTRATION,
  TRY_REGISTER_LATER,
  TRY_REGISTER_LATER_USER,
  UPGRADE_TIER_PATH,
  WS_NOTIFICATIONS
} from '../../constants/routes'
import { Button, Message, Spinner } from '../../ui'
import { loadExtRefId, loadRefId } from '../../utils/storage'
import RegisterSuccessMessage from './RegisterSuccessMessage/RegisterSuccessMessage'
import { Redirect, useLocation } from 'react-router-dom'
import { ResendEmailForm } from './ResendEmailForm/ResendEmailForm'
import { Tier1Register } from '../Tier1Register/Tier1Register'
import { useStore } from '../../StateProvider'
import {
  fetchQuestionnaireData, postRegisterData, resendTokenApi, checkLogin, questionnaireCheck, tryLater
} from '../api'
import { getQuestionnaireData } from '../utils/utils'
import { ErrorMessage } from '../ErrorMessage/ErrorMessage'
import { DisabledMessage } from '../DisabledMessage/DisabledMessage'
import { ButtonLink } from '../../ui/ButtonLink/ButtonLink'
import { SOCKET_MESSAGES, TARGET_PLATFORMS } from '../../constants/constants'
import { QuestionnaireFailMessage } from './QuestionnaireFailMessage/QuestionnaireFailMessage'
import cn from 'classnames'
import { getTextByLang } from '../ExternalFields'
import { useParamsObj } from '../utils/hooks'
import { isNil } from 'ramda'
import styles from './style.css'
import { useGetPlatform } from '../ExternalFields/utils'
import { pushEvent } from '../utils/googleTag'

/**
 * @param {{resendToken: func, disabledResend: boolean, isLoadingResend: boolean}} props
 */
export const ScanErrorMessage = ({ resendToken, disabledResend, isLoadingResend }) => {
  const { t } = useTranslation()
  return (
    <Message title={t('createAccount.alreadyExistsTitle')} type='failed'>
      {t('createAccount.scanError')}
      <Button onClick={resendToken} disabled={disabledResend} isLoading={isLoadingResend}>
        {t('createAccount.resend')}
      </Button>
    </Message>
  )
}

export const ScanPendingMessage = () => {
  const { t } = useTranslation()
  const [
    {
      whitelabel: { email, phone }
    }
  ] = useStore()
  return (
    <Message title={t('createAccount.alreadyExistsTitle')} type='failed'>
      {t('createAccount.scanPending')} <a href={`mailto:${email}`}>{email}</a>, <br />
      {phone}
    </Message>
  )
}

export const ScanDoneMessage = () => {
  const { t } = useTranslation()
  const [
    {
      whitelabel: { email, phone }
    }
  ] = useStore()
  return (
    <Message title={t('createAccount.alreadyExistsTitle')} type='failed'>
      {t('createAccount.scanDone')} <a href={`mailto:${email}`}>{email}</a>, {phone}
    </Message>
  )
}

export const GetDataMessage = () => {
  const { t } = useTranslation()
  const [
    {
      whitelabel: { email, phone }
    }
  ] = useStore()
  return (
    <Message title={t('createAccount.alreadyExistsTitle')} type='failed'>
      {t('createAccount.dataError')}: <a href={`mailto:${email}`}>{email}</a>, {phone}
    </Message>
  )
}

export const ErpDoneMessage = () => {
  const { t } = useTranslation()
  const [
    {
      whitelabel: { email, phone, whiteLabelName, loginURL }
    }
  ] = useStore()
  return (
    <Message title={t('createAccount.identityVerified')} type='success'>
      {t('createAccount.loginTo')} <a href={loginURL}>{whiteLabelName}</a>.{' '}
      {t('createAccount.contactSupport')}: <a href={`mailto:${email}`}>{email}</a>,
      <br /> {phone}
    </Message>
  )
}

export const AlreadyExistSameMktdMessage = () => {
  const [{ whitelabel: { loginURL } }] = useStore()
  const { t } = useTranslation()

  return (
    <Message
      title={t('createAccount.haveValidDemoAcc')}
      type='success'
    >
      <Trans i18nKey='createAccount.checkEmailDetails'>
        Please click <a href={loginURL}>here</a> to open a live account.
      </Trans>
    </Message>
  )
}

export const AlreadyExistSameExpiredMktdMessage = () => {
  const [{ whitelabel: { loginURL } }] = useStore()
  const { t } = useTranslation()

  return (
    <Message
      title={t('createAccount.demoExpired')}
      type='failed'
    >
      <Trans i18nKey='createAccount.checkEmailDetails'>
        Please click <a href={loginURL}>here</a> to open a live account.
      </Trans>
    </Message>
  )
}

export const NoQuestionnaireMessage = () => {
  const { t } = useTranslation()
  return <Message title={t('createAccount.noQuestionnaire')} type='failed' />
}

export const QuestionnaireTryLaterMessage = () => {
  const { t } = useTranslation()
  const [{ whitelabel: { whiteLabelName, customTexts } }] = useStore()
  const copyright = getTextByLang('copyright', customTexts) || whiteLabelName
  return <Message title={t('createAccount.questionnaireProceedLaterMessage', { copyright })} type='success' />
}

const WrongWlEmailMessage = ({ error, onLinkClick }) => {
  const { t } = useTranslation()
  const [, dispatch] = useStore()

  const onClick = () => {
    dispatch({ type: 'clearForm' })
    onLinkClick()
  }

  return (
    <Message title={error?.message} type='failed'>
      {t('createAccount.noMessageForWLText')}
      <ButtonLink onClick={onClick}>
        {t('createAccount.noMessageForWLLink')}
      </ButtonLink>
    </Message>
  )
}

const RegisterOperatorMessage = ({ error }) => {
  const [
    {
      whitelabel: {
        customTexts,
        whiteLabelName
      }
    }
  ] = useStore()
  const { t } = useTranslation()

  const title = useMemo(() => {
    const copyright = getTextByLang('copyright', customTexts) || whiteLabelName

    if (error) {
      return t(error)
    }

    return (
      <>
        {t('createAccount.checkEmailSt1', { copyright })}
        <br />
        {t('createAccount.checkEmailOperatorP2')}
        <br />
        {t('createAccount.checkEmailOperatorP3')}
      </>
    )
  }, [t, error])

  return <Message title={title} type={error ? 'failed' : 'mail'} />
}

const STATUSES = {
  ERP_PENDING: 'ERP_PENDING',
  ERP_ERROR: 'ERP_ERROR',
  ERP_DONE: 'ERP_DONE',
  GET_DATA_PENDING: 'GET_DATA_PENDING',
  GET_DATA_DONE: 'GET_DATA_DONE',
  GET_DATA_ERROR: 'GET_DATA_ERROR',
  ALREADY_EXIST: 'ALREADY_EXIST',
  SCAN_ERROR: 'SCAN_ERROR',
  SCAN_PENDING: 'SCAN_PENDING',
  SCAN_DONE: 'SCAN_DONE',
  CREATED: 'CREATED',
  ALREADY_EXIST_SAME: 'ALREADY_EXIST_SAME',
  ALREADY_EXIST_OTHER: 'ALREADY_EXIST_OTHER',
  CREATED_WITH_REMARKS: 'CREATED_WITH_REMARKS',
  NO_QUESTIONNAIRE: 'NO_QUESTIONNAIRE',
  REQUEST_ERROR: 'REQUEST_ERROR',
  REQUEST_ERROR_WRONG_WL_EMAIL: 'REQUEST_ERROR_WRONG_WL_EMAIL',
  ERROR: 'ERROR',
  ALREADY_EXIST_SAME_MKTD: 'ALREADY_EXIST_SAME_MKTD',
  ALREADY_EXIST_SAME_EXPIRED: 'ALREADY_EXIST_SAME_EXPIRED',
  QUESTIONNAIRE_FAIL: 'QUESTIONNAIRE_FAIL',
  QUESTIONNAIRE_TRY_LATER: 'QUESTIONNAIRE_TRY_LATER',
  DISABLED: 'DISABLED'
}

const initialState = {
  email: '',
  disabledResend: false,
  created: false,
  resent: false,
  requestError: {},
  isLoadingResend: false,
  status: '',
  emailWrongWlError: {},
  notificationToken: null,
  emailConfirmedError: ''
}

const getRefId = () => {
  const loadedRefId = loadRefId()
  // 'undefined' is temp fix for users with current browser session
  return loadedRefId && loadedRefId !== 'undefined' ? { refId: loadedRefId } : {}
}

const getExtRefId = () => {
  const loadedExtRefId = loadExtRefId()
  // 'undefined' is temp fix for users with current browser session
  return loadedExtRefId && loadedExtRefId !== 'undefined' ? { extRefId: loadedExtRefId } : {}
}

const CreateAccount = ({ processingMode }) => {
  const location = useLocation()
  const { i18n } = useTranslation()
  const [{
    whitelabel: { redirectToTier2FormAfterCreate, extFields, targetPlatform, questionnaireTier, webSocketNotificationEnabled, googleTagType, immediateLoginEnabled },
    form,
    form: { accId },
    user: { brokerGroup, utmCampaign, deptCode, fundId, tier, ctierName }
  }, dispatch] = useStore()
  const { alreadyExistsDiffError, ctierEnLabel } = form[accId]
  const [stateManager, setStateManager] = useState(initialState)
  const [loginUrl, setLoginUrl] = useState('')
  const [externalFieldsValues, setExternalFieldsValues] = useState({})
  const platform = useGetPlatform()
  const paramsObj = useParamsObj({ brokerGroup, utmCampaign, deptCode, fundId, search: location.search, platform })

  useEffect(() => {
    dispatch({ type: 'setTier', tier: 1 })
    return () => {
      dispatch({ type: 'clearValidation', tier: 1 })
    }
  }, [])

  useEffect(() => {
    /**
     * @param {{ string: SelectItem[]}} data
     */
    const fetchQuestionnaireDataCallback = (data) => {
      dispatch({ type: 'setData', data, name: 'questionnaire' })
    }
    if (i18n.language && questionnaireTier === 1) {
      fetchQuestionnaireData().then(fetchQuestionnaireDataCallback)
    }
  }, [i18n.language, questionnaireTier])

  useEffect(() => {
    if (alreadyExistsDiffError) {
      dispatch({ type: 'setExtFieldValue', name: 'loginExistsError', value: false })

      checkIfLoginAvailable(externalFieldsValues.email, externalFieldsValues.dateOfBirth)
        .then((response) => {
          if (response && !response.available) {
            dispatch({ type: 'setExtFieldValue', name: 'loginExistsError', value: true })
            dispatch({ type: 'setExtFieldValue', name: 'proposed', value: response.proposed })
          }
        })
    }
  }, [alreadyExistsDiffError])

  useEffect(() => {
    if (webSocketNotificationEnabled && !isNil(stateManager.notificationToken) && (stateManager.created || stateManager.resent)) {
      // eslint-disable-next-line no-undef
      const socket = new WebSocket(`${WS_NOTIFICATIONS}?notificationToken=${stateManager.notificationToken}`)

      socket.onopen = () => {
        console.log('WS connection opened')
      }

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data)

        if (data.operation === SOCKET_MESSAGES.EMAIL_CONFIRMED && data.url) {
          console.log('Email confirmed')
          window.location.href = data.url
        }

        if (data.operation.includes('_ERROR')) {
          console.log('Email error')
          setStateManager(prevState => ({ ...prevState, emailConfirmedError: data.message || 'createAccount.error' }))
        }
      }

      socket.onerror = error => {
        console.log(error.message)
      }

      socket.onclose = () => {
        console.log('WS connection closed')
      }

      return () => {
        socket.close()
      }
    }
  }, [webSocketNotificationEnabled, stateManager.created, stateManager.resent, stateManager.notificationToken])

  const initialRegister = (refId, extRefId, addParams, formFields) => {
    /**
     * @param {ErrorCommon} requestError
     */
    const reject = (requestError) => {
      const errorStatus = errorStatusHandler(requestError)
      setStateManager(prevState => (
        { ...prevState, requestError, status: errorStatus }
      ))
    }

    const pushFormEnd = () => pushEvent('formEnd', {
      formType: googleTagType,
      formCategory: ctierEnLabel || ctierName,
      formCountry: formFields?.residenceCountry,
      formCurrency: formFields?.depositCurrency
    })

    const resolve = ({ result, notificationToken }) => {
      switch (result) {
        case STATUSES.CREATED: {
          pushFormEnd()
          notificationToken
            ? setStateManager(prevState => ({ ...prevState, created: true, notificationToken }))
            : setStateManager(prevState => ({ ...prevState, created: true }))
          break
        }
        case STATUSES.CREATED_WITH_REMARKS: {
          pushFormEnd()
          notificationToken
            ? setStateManager(prevState => ({ ...prevState, created: true, notificationToken }))
            : setStateManager(prevState => ({ ...prevState, created: true }))
          break
        }
        case STATUSES.ALREADY_EXIST_SAME: {
          processAlreadyExistSame(result)
          break
        }
        case STATUSES.ALREADY_EXIST_OTHER: {
          processAlreadyExistOther(result)
          break
        }
        default:
          setStateManager(prevState => ({ ...prevState, status: result }))
      }
    }
    const url = tier === 0 ? RENEW_REGISTRATION : INITIAL_REGISTRATION
    const processingModeParam = processingMode ? { processingMode: 'M' } : {}
    return postRegisterData(url, {
      ...refId,
      ...extRefId,
      ...addParams,
      ...formFields,
      ...processingModeParam,
      continueOnQuestionnaireFail: true
    })
      .then((result) => {
        if (tier !== 0 && result?.userDetails?.loginUrl && immediateLoginEnabled) {
          setLoginUrl(result.userDetails.loginUrl)
        }
        return result
      })
      .then(resolve)
      .catch(reject)
  }

  const processAlreadyExistSame = (result) => {
    if (targetPlatform === TARGET_PLATFORMS.MKTD) {
      setStateManager(prevState => (
        { ...prevState, status: STATUSES.ALREADY_EXIST_SAME_MKTD }
      ))
    } else {
      dispatch({ type: 'setExtFieldValue', name: 'alreadyExistsSameError', value: true })
      setStateManager(prevState => ({ ...prevState, status: result }))
    }
  }

  const processAlreadyExistOther = (result) => {
    dispatch({ type: 'setExtFieldValue', name: 'alreadyExistsDiffError', value: true })
    setStateManager(prevState => ({ ...prevState, status: result }))
  }

  const checkIfLoginAvailable = (login, dateOfBirth = '') => {
    /**
     * @param {ErrorCommon} requestError
     */
    const reject = (requestError) => {
      setStateManager(prevState => ({ ...prevState, requestError, status: STATUSES.REQUEST_ERROR }))
    }

    const resolve = (response) => {
      return response
    }

    return checkLogin(login, dateOfBirth).then(resolve, reject)
  }

  const onSignUpWithQuestionnaire = (externalFldValues) => {
    setExternalFieldsValues(externalFldValues)
    if (questionnaireTier === 1) {
      const questionnaireData = getQuestionnaireData(extFields, externalFldValues, 1)

      return questionnaireCheck(questionnaireData).then(
        ({ passed }) => {
          if (passed) {
            return onSignUp(externalFldValues)
          } else {
            dispatch({ type: 'setExtFieldValue', name: 'alreadyExistsDiffError', value: false })
            setStateManager(prevState => ({ ...prevState, status: STATUSES.QUESTIONNAIRE_FAIL }))
            return Promise.reject(new Error('questionnaire fail'))
          }
        },
        (error) => {
          setStateManager(prevState => (
            { ...prevState, requestError: error, status: STATUSES.REQUEST_ERROR }
          ))
          return Promise.reject(new Error('questionnaire fail'))
        })
    } else {
      return onSignUp(externalFldValues)
    }
  }

  const onSignUp = useCallback((externalFldValues) => {
    dispatch({ type: 'setExtFieldValue', name: 'loginExistsError', value: false })
    const formFields = externalFldValues || externalFieldsValues
    setStateManager(prevState => ({ ...prevState, email: formFields.email }))
    const refId = getRefId()
    const extRefId = getExtRefId()
    if (!alreadyExistsDiffError) {
      return initialRegister(refId, extRefId, paramsObj, formFields)
    }

    return checkIfLoginAvailable(formFields.login, formFields.dateOfBirth).then(({ available, proposed }) => {
      if (available) {
        return initialRegister(refId, extRefId, paramsObj, formFields)
      } else {
        dispatch({ type: 'setExtFieldValue', name: 'loginExistsError', value: true })
        dispatch({ type: 'setExtFieldValue', name: 'proposed', value: proposed })
        return Promise.reject(new Error('login not available'))
      }
    })
  }, [alreadyExistsDiffError, externalFieldsValues, paramsObj, ctierEnLabel, googleTagType, ctierName])

  /**
   * @param {?string} email
   */
  const resendToken = (email) => () => {
    email = email || stateManager.email

    const reject = (requestError) => {
      if (requestError.code === 422) {
        setStateManager(prevState => (
          { ...prevState, requestError: requestError, status: STATUSES.REQUEST_ERROR_WRONG_WL_EMAIL }
        ))
        dispatch({ type: 'clearValidation', tier: 1 })
        return
      }
      setStateManager(prevState => (
        { ...prevState, requestError, status: STATUSES.REQUEST_ERROR }
      ))
      dispatch({ type: 'clearValidation', tier: 1 })
    }

    const resolve = () => setStateManager(prevState => ({ ...prevState, resent: true }))

    setStateManager(prevState => ({ ...prevState, disabledResend: true, isLoadingResend: true }))
    resendTokenApi(email).then(resolve, reject)
  }

  const onReturnClick = () => {
    setStateManager(initialState)
  }

  const onQuestionnaireContinue = () => {
    onSignUp()
  }

  const onQuestionnaireTryLater = () => {
    const url = tier === 0 ? TRY_REGISTER_LATER_USER : TRY_REGISTER_LATER
    tryLater({ ...externalFieldsValues, ...paramsObj }, url).then(({ result }) => {
      switch (result) {
        case STATUSES.ALREADY_EXIST_SAME: {
          processAlreadyExistSame(result)
          break
        }
        case STATUSES.ALREADY_EXIST_OTHER: {
          processAlreadyExistOther(result)
          break
        }
        default:
          setStateManager(prevState => ({ ...prevState, status: STATUSES.QUESTIONNAIRE_TRY_LATER }))
      }
    }, error => {
      const errorStatus = errorStatusHandler(error)
      setStateManager(prevState => (
        { ...prevState, requestError: error, status: errorStatus }
      ))
    })
  }

  const errorStatusHandler = (requestError) => {
    let errorStatus
    switch (requestError.code) {
      case 1013:
      case 1014:
        errorStatus = STATUSES.DISABLED
        break
      default:
        errorStatus = STATUSES.REQUEST_ERROR
    }

    return errorStatus
  }

  if (loginUrl) {
    window.location.href = loginUrl
    return <Spinner />
  }

  const {
    created,
    resent,
    disabledResend,
    isLoadingResend,
    status,
    requestError,
    emailConfirmedError
  } = stateManager

  if (created || resent) {
    if (!redirectToTier2FormAfterCreate) {
      if (webSocketNotificationEnabled) {
        return <RegisterOperatorMessage error={emailConfirmedError} />
      }

      return (
        <RegisterSuccessMessage
          accountCreatedWithRemarks={status === STATUSES.CREATED_WITH_REMARKS}
        />)
    } else {
      return <Redirect to={{ pathname: UPGRADE_TIER_PATH }} />
    }
  }

  switch (status) {
    case STATUSES.SCAN_ERROR:
      return (
        <ScanErrorMessage
          resendToken={resendToken()}
          disabledResend={disabledResend}
          isLoadingResend={isLoadingResend}
        />)
    case STATUSES.SCAN_PENDING:
      return <ScanPendingMessage />
    case STATUSES.SCAN_DONE:
    case STATUSES.GET_DATA_PENDING:
    case STATUSES.GET_DATA_DONE:
    case STATUSES.ERP_PENDING:
      return <ScanDoneMessage />
    case STATUSES.GET_DATA_ERROR:
    case STATUSES.ERP_ERROR:
      return <GetDataMessage />
    case STATUSES.ERP_DONE:
      return <ErpDoneMessage />
    case STATUSES.NO_QUESTIONNAIRE:
      return <NoQuestionnaireMessage />
    case STATUSES.ERROR:
      return <ErrorMessage />
    case STATUSES.REQUEST_ERROR:
      return <ErrorMessage error={requestError} />
    case STATUSES.REQUEST_ERROR_WRONG_WL_EMAIL:
      return <WrongWlEmailMessage error={requestError} onLinkClick={onReturnClick} />
    case STATUSES.ALREADY_EXIST_SAME_MKTD:
      return <AlreadyExistSameMktdMessage />
    case STATUSES.ALREADY_EXIST_SAME_EXPIRED:
      return <AlreadyExistSameExpiredMktdMessage />
    case STATUSES.QUESTIONNAIRE_TRY_LATER:
      return <QuestionnaireTryLaterMessage />
    case STATUSES.DISABLED:
      return <DisabledMessage />
    default:
      return (
        <>
          <div className={cn({ [styles.hidden]: status === STATUSES.QUESTIONNAIRE_FAIL })}>
            <Tier1Register
              onSignUp={onSignUpWithQuestionnaire}
            />
          </div>
          {status === STATUSES.QUESTIONNAIRE_FAIL && (
            <QuestionnaireFailMessage
              onContinue={onQuestionnaireContinue}
              onTryLater={onQuestionnaireTryLater}
            />)}
          {!redirectToTier2FormAfterCreate && targetPlatform !== TARGET_PLATFORMS.MKTD && (
            <ResendEmailForm resendToken={resendToken} isLoadingResend={isLoadingResend} />)}
        </>)
  }
}

export { CreateAccount }
